import type { CategoryId } from './types'
import { MAX_ATTEMPTS } from './constants'

/** Per-category lifetime stats, persisted in localStorage. */
export interface CategoryStats {
  played: number
  wins: number
  curStreak: number
  maxStreak: number
  /** dist[i] = puzzles solved using i+1 checks. */
  dist: number[]
  /** dayIndex of the last recorded result (for idempotency). */
  lastDay: number
  /** dayIndex of the last win (streak bookkeeping). */
  lastWonDay: number
}

const VERSION = 'v1'

function key(category: CategoryId): string {
  return `lineup:stats:${VERSION}:${category}`
}

function empty(): CategoryStats {
  return {
    played: 0,
    wins: 0,
    curStreak: 0,
    maxStreak: 0,
    dist: Array(MAX_ATTEMPTS).fill(0),
    lastDay: -1,
    lastWonDay: -2,
  }
}

export function readStats(category: CategoryId): CategoryStats {
  try {
    const raw = localStorage.getItem(key(category))
    if (!raw) return empty()
    const s = JSON.parse(raw) as CategoryStats
    // tolerate older/short dist arrays
    if (!Array.isArray(s.dist) || s.dist.length < MAX_ATTEMPTS) {
      s.dist = Object.assign(Array(MAX_ATTEMPTS).fill(0), s.dist ?? [])
    }
    return s
  } catch {
    return empty()
  }
}

/**
 * Records a finished daily result. Idempotent per (category, day): calling it
 * again for the same day is a no-op, so restoring a finished puzzle on refresh
 * never double-counts.
 */
export function recordResult(
  category: CategoryId,
  day: number,
  won: boolean,
  attemptsUsed: number,
): CategoryStats {
  const s = readStats(category)
  if (s.lastDay === day) return s // already counted today

  s.played += 1
  if (won) {
    s.wins += 1
    s.curStreak = s.lastWonDay === day - 1 ? s.curStreak + 1 : 1
    s.maxStreak = Math.max(s.maxStreak, s.curStreak)
    s.lastWonDay = day
    const idx = Math.min(Math.max(attemptsUsed - 1, 0), MAX_ATTEMPTS - 1)
    s.dist[idx] += 1
  } else {
    s.curStreak = 0
  }
  s.lastDay = day

  try {
    localStorage.setItem(key(category), JSON.stringify(s))
  } catch {
    /* ignore */
  }
  return s
}

/** Display streak: a win that isn't today or yesterday is already broken. */
export function effectiveStreak(s: CategoryStats, today: number): number {
  return s.lastWonDay >= today - 1 ? s.curStreak : 0
}

export function winPercent(s: CategoryStats): number {
  return s.played ? Math.round((s.wins / s.played) * 100) : 0
}
